import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { HandHeart, Upload, CheckCircle, ArrowRight, ArrowLeft, FileText, User, Clock, Play, Pause, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRAND_NAME = "Seniors Helping Seniors\u00ae Union & Somerset County NJ";
const BRAND_NAME_SHORT = "SHS\u00ae Union & Somerset County NJ";

const COMMUNICATION_METHODS = [
  { id: 'phone', label: 'Phone Call' },
  { id: 'text', label: 'Text Message' },
  { id: 'email', label: 'Email' },
];

const ApplicationFlow = () => {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const referralCode = searchParams.get('ref');
  const resumeCandidateId = searchParams.get('resume');
  
  const [step, setStep] = useState('welcome'); // welcome, personal_info, resume, assessment_prep, questions, complete, disqualified
  const [job, setJob] = useState(null);
  const [candidateId, setCandidateId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [screeningQuestions, setScreeningQuestions] = useState([]);
  const [resumeFile, setResumeFile] = useState(null);
  const [parsedResume, setParsedResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [disqualifyMessage, setDisqualifyMessage] = useState('');
  const [showLookup, setShowLookup] = useState(false);
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const autosaveRef = useRef(null);
  const hasStartedAssessment = useRef(false);
  const [showPauseConfirm, setShowPauseConfirm] = useState(false);
  const [pauseSaving, setPauseSaving] = useState(false);

  // Personal info fields
  const [personalInfo, setPersonalInfo] = useState({
    first_name: '',
    last_name: '',
    email: '',
    home_phone: '',
    mobile_phone: '',
    preferred_communication: [],
    company_url: ''  // Honeypot field
  });
  const [personalInfoErrors, setPersonalInfoErrors] = useState({});

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const [jobRes, questionsRes] = await Promise.all([
          axios.get(`${API}/jobs/${jobId}`),
          axios.get(`${API}/screening-questions/public`)
        ]);
        setJob(jobRes.data);
        // Replace {{company_name}} in question text
        const questions = questionsRes.data.map(q => ({
          ...q,
          id: q.question_id,
          question: q.question.replace(/\{\{company_name\}\}/g, BRAND_NAME),
          disqualifyMessage: q.disqualify_message,
          minLength: q.min_length,
          min: q.min_val,
          max: q.max_val,
        }));
        setScreeningQuestions(questions);

        // Check if resuming an in-progress assessment
        if (resumeCandidateId) {
          await resumeAssessment(resumeCandidateId);
        }
      } catch (error) {
        toast.error('Job not found');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [jobId, navigate, resumeCandidateId]);

  // Autosave on beforeunload and send pause notification
  useEffect(() => {
    const handleBeforeUnload = async () => {
      if (candidateId && step === 'questions' && Object.keys(answers).length > 0) {
        // Fire-and-forget saves using sendBeacon
        const progressData = JSON.stringify({
          answers,
          current_question: currentQuestion,
          total_questions: screeningQuestions.length
        });
        navigator.sendBeacon(
          `${API}/candidates/${candidateId}/save-progress`,
          new Blob([progressData], { type: 'application/json' })
        );
        // Also trigger pause notification
        navigator.sendBeacon(
          `${API}/candidates/${candidateId}/pause-notify`,
          new Blob(['{}'], { type: 'application/json' })
        );
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [candidateId, step, answers, currentQuestion]);

  const resumeAssessment = async (cId) => {
    try {
      const res = await axios.get(`${API}/candidates/${cId}/progress`);
      const data = res.data;
      if (data.status !== 'in_progress') {
        toast.info('This assessment has already been submitted.');
        return;
      }
      setCandidateId(data.candidate_id);
      setAnswers(data.answers || {});
      hasStartedAssessment.current = true;

      // Restore personal info email for pause confirmation display
      if (data.contact_info?.email) {
        setPersonalInfo(prev => ({ ...prev, email: data.contact_info.email }));
      }

      // Determine which step to resume at
      if (data.progress?.current_question > 0 || Object.keys(data.answers || {}).length > 0) {
        setCurrentQuestion(data.progress?.current_question || 0);
        setStep('questions');
        toast.success(`Welcome back! Resuming at question ${(data.progress?.current_question || 0) + 1}.`);
      } else if (data.has_resume) {
        setStep('assessment_prep');
      } else if (data.contact_info?.email) {
        setStep('resume');
      } else {
        setStep('personal_info');
      }
    } catch (error) {
      toast.error('Could not resume assessment. Please start a new application.');
    }
  };

  const lookupAssessment = async () => {
    if (!lookupEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    setLookingUp(true);
    try {
      const res = await axios.post(`${API}/candidates/lookup`, { email: lookupEmail.trim() });
      // Redirect to resume URL
      navigate(`/apply/${res.data.job_id}?resume=${res.data.candidate_id}`);
      toast.success('Assessment found! Loading your progress...');
    } catch (error) {
      toast.error('No in-progress assessment found for this email. Please start a new application.');
    } finally {
      setLookingUp(false);
    }
  };

  const saveAndContinueLater = async () => {
    setPauseSaving(true);
    try {
      // Save current progress
      await axios.post(`${API}/candidates/${candidateId}/save-progress`, {
        answers,
        current_question: currentQuestion,
        total_questions: screeningQuestions.length
      });
      // Send pause notification with resume link
      await axios.post(`${API}/candidates/${candidateId}/pause-notify`);
      setShowPauseConfirm(true);
    } catch (error) {
      toast.error('Failed to save. Please try again.');
    } finally {
      setPauseSaving(false);
    }
  };

  const startApplication = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/candidates/start`, {
        job_id: jobId,
        referral_code: referralCode
      });
      setCandidateId(response.data.candidate_id);
      setStep('personal_info');
    } catch (error) {
      toast.error('Failed to start application');
    } finally {
      setLoading(false);
    }
  };

  const updatePersonalField = (field, value) => {
    setPersonalInfo(prev => ({ ...prev, [field]: value }));
    if (personalInfoErrors[field]) {
      setPersonalInfoErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const toggleCommunicationMethod = (method) => {
    setPersonalInfo(prev => {
      const current = prev.preferred_communication;
      if (current.includes(method)) {
        return { ...prev, preferred_communication: current.filter(m => m !== method) };
      }
      return { ...prev, preferred_communication: [...current, method] };
    });
    if (personalInfoErrors.preferred_communication) {
      setPersonalInfoErrors(prev => ({ ...prev, preferred_communication: null }));
    }
  };

  const validatePersonalInfo = () => {
    const errors = {};
    if (!personalInfo.first_name.trim()) errors.first_name = 'First name is required';
    if (!personalInfo.last_name.trim()) errors.last_name = 'Last name is required';
    if (!personalInfo.email.trim()) errors.email = 'Email address is required';
    else if (!/\S+@\S+\.\S+/.test(personalInfo.email)) errors.email = 'Please enter a valid email';
    if (!personalInfo.home_phone.trim() && !personalInfo.mobile_phone.trim()) {
      errors.home_phone = 'At least one phone number is required';
      errors.mobile_phone = 'At least one phone number is required';
    }
    if (personalInfo.preferred_communication.length === 0) {
      errors.preferred_communication = 'Please select at least one communication method';
    }
    setPersonalInfoErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submitPersonalInfo = async () => {
    if (!validatePersonalInfo()) return;

    // Honeypot check — if filled, silently "succeed" without saving
    if (personalInfo.company_url) {
      toast.success('Information saved!');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/candidates/${candidateId}/personal-info`, {
        first_name: personalInfo.first_name.trim(),
        last_name: personalInfo.last_name.trim(),
        email: personalInfo.email.trim(),
        home_phone: personalInfo.home_phone.trim() || null,
        mobile_phone: personalInfo.mobile_phone.trim() || null,
        preferred_communication: personalInfo.preferred_communication
      });
      toast.success('Information saved!');
      setStep('resume');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save personal info');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 15 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large. Please upload a file under 15MB.');
      return;
    }

    setResumeFile(file);
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/candidates/${candidateId}/resume`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setParsedResume(response.data.parsed);
      toast.success('Resume uploaded successfully!');
      
      // Go to assessment prep page instead of directly to questions
      setTimeout(() => setStep('assessment_prep'), 1500);
    } catch (error) {
      toast.error('Failed to upload resume');
    } finally {
      setSubmitting(false);
    }
  };

  const autosaveProgress = useCallback(async (updatedAnswers, questionIndex) => {
    if (!candidateId) return;
    // Debounce: clear previous timer
    if (autosaveRef.current) clearTimeout(autosaveRef.current);
    autosaveRef.current = setTimeout(async () => {
      try {
        await axios.post(`${API}/candidates/${candidateId}/save-progress`, {
          answers: updatedAnswers,
          current_question: questionIndex,
          total_questions: screeningQuestions.length
        });
      } catch (e) {
        console.error('Autosave failed:', e);
      }
    }, 1000); // 1 second debounce
  }, [candidateId]);

  const handleAnswer = useCallback((questionId, value) => {
    setAnswers(prev => {
      const updated = { ...prev, [questionId]: value };
      autosaveProgress(updated, currentQuestion);
      return updated;
    });
  }, [autosaveProgress, currentQuestion]);

  const handleCheckboxAnswer = useCallback((questionId, option, checked) => {
    setAnswers(prev => {
      const current = prev[questionId] || [];
      const updated = checked
        ? { ...prev, [questionId]: [...current, option] }
        : { ...prev, [questionId]: current.filter(o => o !== option) };
      autosaveProgress(updated, currentQuestion);
      return updated;
    });
  }, [autosaveProgress, currentQuestion]);

  const goToNextQuestion = async () => {
    const question = screeningQuestions[currentQuestion];
    const answer = answers[question.id];
    
    // Check for disqualification
    if (question.disqualify && answer === question.disqualify) {
      setDisqualifyMessage(question.disqualifyMessage);
      setStep('disqualified');
      return;
    }
    
    // Validate required fields
    if (question.required) {
      if (!answer || (typeof answer === 'string' && answer.trim() === '')) {
        toast.error('Please answer this question to continue');
        return;
      }
      if (question.type === 'textarea' && question.minLength && answer.length < question.minLength) {
        toast.error(`Please provide at least ${question.minLength} characters`);
        return;
      }
    }
    
    if (currentQuestion < screeningQuestions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      await submitApplication();
    }
  };

  const goToPrevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const submitApplication = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/candidates/${candidateId}/answers`, {
        candidate_id: candidateId,
        answers
      });
      
      await axios.post(`${API}/candidates/${candidateId}/submit`);
      
      setStep('complete');
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  // Total steps: personal_info (5%), resume (10%), assessment_prep (12%), questions (15-95%), complete (100%)
  const progress = step === 'personal_info' ? 5
    : step === 'resume' ? 10
    : step === 'assessment_prep' ? 12
    : step === 'questions' ? Math.round(15 + ((currentQuestion + 1) / screeningQuestions.length) * 80)
    : step === 'complete' ? 100 : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#4B2D6E] border-t-transparent mx-auto mb-4"></div>
          <p className="text-[#666666]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Header */}
      <header className="bg-[#4B2D6E] text-white py-4 px-6 shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <HandHeart className="h-8 w-8 text-[#E8722A] flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <h1 className="font-bold text-sm sm:text-base lg:text-lg font-[Lato] leading-tight text-white" data-testid="header-brand-name">
              <span className="hidden sm:inline">{BRAND_NAME}</span>
              <span className="sm:hidden">{BRAND_NAME_SHORT}</span>
            </h1>
            <p className="text-xs sm:text-sm text-gray-300 truncate">Application for {job?.title}</p>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      {step !== 'welcome' && step !== 'disqualified' && (
        <div className="bg-white border-b px-6 py-3">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#666666]">Application Progress</span>
              <span className="text-sm font-semibold text-[#4B2D6E]">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2 bg-gray-200" />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Welcome Step */}
        {step === 'welcome' && (
          <Card className="animate-fade-in border-t-4 border-[#4B2D6E]" data-testid="welcome-card">
            <CardHeader className="text-center pb-2">
              <div className="w-20 h-20 bg-[#E8722A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <HandHeart className="h-10 w-10 text-[#E8722A]" />
              </div>
              <CardTitle className="text-3xl text-[#4B2D6E] font-[Lato]">Welcome!</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-6">
              <div className="bg-[#F5F5F5] rounded-lg p-6 text-left">
                <p className="text-lg text-[#333333] mb-4">
                  Welcome to the <strong>{BRAND_NAME}</strong> {job?.title} Application.
                </p>
                <p className="text-[#666666] mb-4">
                  We're so glad you're here! This process starts with a few quick steps, followed by a screening assessment that takes approximately <strong>20-30 minutes</strong>.
                </p>
                <p className="text-[#666666]">
                  Please have your <strong>resume ready to upload</strong>. You can pause the assessment anytime and come back later. Let's get started!
                </p>
              </div>
              <Button 
                onClick={startApplication}
                className="bg-[#E8722A] hover:bg-[#D6621F] text-white text-lg font-bold py-6 px-12 rounded-full"
                disabled={submitting}
                data-testid="begin-application-btn"
              >
                Begin Application
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              {/* Continue Assessment Lookup */}
              <div className="pt-4 border-t border-gray-200 mt-6">
                {!showLookup ? (
                  <button
                    onClick={() => setShowLookup(true)}
                    className="text-sm text-[#4B2D6E] hover:text-[#E8722A] underline transition-colors"
                    data-testid="continue-assessment-link"
                  >
                    Already started? Continue your assessment
                  </button>
                ) : (
                  <div className="bg-white rounded-lg p-4 border space-y-3 text-left">
                    <p className="text-sm font-medium text-[#333]">Enter the email you used to start your application:</p>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        value={lookupEmail}
                        onChange={(e) => setLookupEmail(e.target.value)}
                        placeholder="your.email@example.com"
                        className="h-11 text-base"
                        onKeyDown={(e) => e.key === 'Enter' && lookupAssessment()}
                        data-testid="lookup-email-input"
                      />
                      <Button
                        onClick={lookupAssessment}
                        disabled={lookingUp}
                        className="bg-[#4B2D6E] hover:bg-[#3a2357] px-6"
                        data-testid="lookup-submit-btn"
                      >
                        {lookingUp ? '...' : <Search className="h-4 w-4" />}
                      </Button>
                    </div>
                    <button
                      onClick={() => { setShowLookup(false); setLookupEmail(''); }}
                      className="text-xs text-[#666] hover:text-[#333] underline"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Personal Info Step */}
        {step === 'personal_info' && (
          <Card className="animate-fade-in border-t-4 border-[#4B2D6E]" data-testid="personal-info-card">
            <CardHeader className="text-center pb-2">
              <div className="w-20 h-20 bg-[#E8722A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="h-10 w-10 text-[#E8722A]" />
              </div>
              <CardTitle className="text-2xl text-[#4B2D6E] font-[Lato]">Tell Us About Yourself</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-center text-[#666666]">
                Let's start with your contact information so we can stay in touch.
              </p>

              {/* Name Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name" className="text-sm font-medium text-[#333] mb-1 block">
                    First Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="first_name"
                    value={personalInfo.first_name}
                    onChange={(e) => updatePersonalField('first_name', e.target.value)}
                    placeholder="First name"
                    className={`h-12 text-base border-2 bg-[#FAFAFA] ${personalInfoErrors.first_name ? 'border-red-400' : 'focus:border-[#4B2D6E]'}`}
                    data-testid="first-name-input"
                  />
                  {personalInfoErrors.first_name && (
                    <p className="text-red-500 text-xs mt-1">{personalInfoErrors.first_name}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="last_name" className="text-sm font-medium text-[#333] mb-1 block">
                    Last Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="last_name"
                    value={personalInfo.last_name}
                    onChange={(e) => updatePersonalField('last_name', e.target.value)}
                    placeholder="Last name"
                    className={`h-12 text-base border-2 bg-[#FAFAFA] ${personalInfoErrors.last_name ? 'border-red-400' : 'focus:border-[#4B2D6E]'}`}
                    data-testid="last-name-input"
                  />
                  {personalInfoErrors.last_name && (
                    <p className="text-red-500 text-xs mt-1">{personalInfoErrors.last_name}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-[#333] mb-1 block">
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={personalInfo.email}
                  onChange={(e) => updatePersonalField('email', e.target.value)}
                  placeholder="your.email@example.com"
                  className={`h-12 text-base border-2 bg-[#FAFAFA] ${personalInfoErrors.email ? 'border-red-400' : 'focus:border-[#4B2D6E]'}`}
                  data-testid="email-input"
                />
                {personalInfoErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{personalInfoErrors.email}</p>
                )}
              </div>

              {/* Phone Numbers */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="home_phone" className="text-sm font-medium text-[#333] mb-1 block">
                    Home Phone
                  </Label>
                  <Input
                    id="home_phone"
                    type="tel"
                    value={personalInfo.home_phone}
                    onChange={(e) => updatePersonalField('home_phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className={`h-12 text-base border-2 bg-[#FAFAFA] ${personalInfoErrors.home_phone ? 'border-red-400' : 'focus:border-[#4B2D6E]'}`}
                    data-testid="home-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="mobile_phone" className="text-sm font-medium text-[#333] mb-1 block">
                    Mobile Phone
                  </Label>
                  <Input
                    id="mobile_phone"
                    type="tel"
                    value={personalInfo.mobile_phone}
                    onChange={(e) => updatePersonalField('mobile_phone', e.target.value)}
                    placeholder="(555) 987-6543"
                    className={`h-12 text-base border-2 bg-[#FAFAFA] ${personalInfoErrors.mobile_phone ? 'border-red-400' : 'focus:border-[#4B2D6E]'}`}
                    data-testid="mobile-phone-input"
                  />
                </div>
              </div>
              {(personalInfoErrors.home_phone || personalInfoErrors.mobile_phone) && !personalInfo.home_phone && !personalInfo.mobile_phone && (
                <p className="text-red-500 text-xs -mt-3">At least one phone number is required</p>
              )}

              {/* Preferred Communication Method */}
              <div>
                <Label className="text-sm font-medium text-[#333] mb-2 block">
                  Preferred Communication Method <span className="text-red-500">*</span>
                  <span className="text-xs text-[#666] font-normal ml-1">(select at least one)</span>
                </Label>
                <div className="flex flex-wrap gap-3">
                  {COMMUNICATION_METHODS.map(method => (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => toggleCommunicationMethod(method.id)}
                      className={`px-5 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        personalInfo.preferred_communication.includes(method.id)
                          ? 'border-[#E8722A] bg-[#E8722A]/10 text-[#E8722A]'
                          : 'border-gray-200 text-[#666] hover:border-[#4B2D6E]/30'
                      }`}
                      data-testid={`comm-method-${method.id}`}
                    >
                      {method.label}
                    </button>
                  ))}
                </div>
                {personalInfoErrors.preferred_communication && (
                  <p className="text-red-500 text-xs mt-1">{personalInfoErrors.preferred_communication}</p>
                )}
              </div>

              {/* Honeypot — hidden from real users */}
              <input
                type="text"
                name="company_url"
                value={personalInfo.company_url}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, company_url: e.target.value }))}
                autoComplete="off"
                tabIndex={-1}
                style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }}
              />

              {/* Continue Button */}
              <div className="flex justify-end pt-4">
                <Button
                  onClick={submitPersonalInfo}
                  disabled={submitting}
                  className="bg-[#E8722A] hover:bg-[#D6621F] text-white font-bold px-10 py-3 rounded-full text-lg"
                  data-testid="personal-info-continue-btn"
                >
                  {submitting ? 'Saving...' : 'Continue'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Resume Upload Step */}
        {step === 'resume' && (
          <Card className="animate-fade-in border-t-4 border-[#4B2D6E]" data-testid="resume-upload-card">
            <CardHeader className="text-center pb-2">
              <div className="w-20 h-20 bg-[#E8722A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-10 w-10 text-[#E8722A]" />
              </div>
              <CardTitle className="text-2xl text-[#4B2D6E] font-[Lato]">Upload Your Resume</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-center text-[#666666]">
                Please upload your resume. We accept PDF, Word (.docx), or image files (max 15MB).
              </p>
              
              <div className="border-2 border-dashed border-[#4B2D6E]/30 rounded-xl p-8 text-center hover:border-[#E8722A] transition-colors">
                <input
                  type="file"
                  accept=".pdf,.docx,.doc,.png,.jpg,.jpeg"
                  onChange={handleResumeUpload}
                  className="hidden"
                  id="resume-upload"
                  data-testid="resume-file-input"
                />
                <label htmlFor="resume-upload" className="cursor-pointer">
                  <Upload className="h-12 w-12 text-[#4B2D6E]/50 mx-auto mb-4" />
                  {resumeFile ? (
                    <div>
                      <p className="text-[#4B2D6E] font-semibold">{resumeFile.name}</p>
                      <p className="text-sm text-[#666666]">Click to upload a different file</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-[#4B2D6E] font-semibold text-lg">Click to upload your resume</p>
                      <p className="text-sm text-[#666666]">or drag and drop</p>
                    </div>
                  )}
                </label>
              </div>

              {submitting && (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#E8722A] border-t-transparent mx-auto mb-2"></div>
                  <p className="text-[#666666]">Processing your resume...</p>
                </div>
              )}

              {parsedResume && (
                <div className="bg-[#4CAF50]/10 border border-[#4CAF50] rounded-lg p-4 flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-[#4CAF50]" />
                  <div>
                    <p className="font-semibold text-[#333333]">Resume received!</p>
                    <p className="text-sm text-[#666666]">Moving to screening questions...</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Assessment Prep Step */}
        {step === 'assessment_prep' && (
          <Card className="animate-fade-in border-t-4 border-[#4B2D6E]" data-testid="assessment-prep-card">
            <CardHeader className="text-center pb-2">
              <div className="w-20 h-20 bg-[#4B2D6E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-10 w-10 text-[#4B2D6E]" />
              </div>
              <CardTitle className="text-2xl text-[#4B2D6E] font-[Lato]">Preliminary Assessment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-[#F5F5F5] rounded-lg p-6 space-y-4">
                <p className="text-lg text-[#333333]">
                  You're about to begin your <strong>Preliminary Assessment</strong> for the <strong>{job?.title}</strong> position.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-2">
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <Clock className="h-6 w-6 text-[#E8722A] flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-[#333]">20-30 minutes</p>
                      <p className="text-xs text-[#666]">Estimated time</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <FileText className="h-6 w-6 text-[#E8722A] flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-[#333]">{screeningQuestions.length} questions</p>
                      <p className="text-xs text-[#666]">5 sections</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <Pause className="h-6 w-6 text-[#E8722A] flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-[#333]">Pause anytime</p>
                      <p className="text-xs text-[#666]">Progress auto-saved</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 border border-[#4B2D6E]/20">
                  <p className="text-sm text-[#666666]">
                    <strong className="text-[#4B2D6E]">No rush!</strong> Your answers are saved automatically as you go. 
                    If you need to step away, simply close the browser. We'll email you a link to pick up right where you left off.
                  </p>
                </div>

                <p className="text-sm text-[#666666]">
                  This assessment helps us understand your experience, availability, and what makes you a great fit for our team. 
                  There are no wrong answers — just be yourself!
                </p>
              </div>

              <div className="text-center">
                <Button 
                  onClick={() => {
                    hasStartedAssessment.current = true;
                    setStep('questions');
                  }}
                  className="bg-[#E8722A] hover:bg-[#D6621F] text-white text-lg font-bold py-6 px-12 rounded-full"
                  data-testid="start-assessment-btn"
                >
                  <Play className="mr-2 h-5 w-5" />
                  Start Assessment
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Questions Step */}
        {step === 'questions' && !showPauseConfirm && (
          <Card className="animate-fade-in border-t-4 border-[#4B2D6E]" data-testid="question-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#666666]">
                  Section {screeningQuestions[currentQuestion].section} - Question {currentQuestion + 1} of {screeningQuestions.length}
                </span>
                <button
                  onClick={saveAndContinueLater}
                  disabled={pauseSaving}
                  className="text-sm text-[#4B2D6E] hover:text-[#E8722A] transition-colors flex items-center gap-1"
                  data-testid="save-continue-later-btn"
                >
                  <Pause className="h-3.5 w-3.5" />
                  {pauseSaving ? 'Saving...' : 'Save & Continue Later'}
                </button>
              </div>
              <CardTitle className="text-xl text-[#4B2D6E] font-[Lato] mt-4">
                {screeningQuestions[currentQuestion].question}
              </CardTitle>
              {/* Autosave indicator */}
              <p className="text-xs text-[#999] mt-1 flex items-center gap-1">
                <CheckCircle className="h-3 w-3 text-green-500" />
                Your progress is saved automatically
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Radio Questions */}
              {screeningQuestions[currentQuestion].type === 'radio' && (
                <RadioGroup
                  value={answers[screeningQuestions[currentQuestion].id] || ''}
                  onValueChange={(value) => handleAnswer(screeningQuestions[currentQuestion].id, value)}
                  className="space-y-3"
                  data-testid="radio-group"
                >
                  {screeningQuestions[currentQuestion].options.map((option) => (
                    <div key={option} className="flex items-center space-x-3 p-4 rounded-lg border-2 border-gray-200 hover:border-[#E8722A] transition-colors cursor-pointer">
                      <RadioGroupItem value={option} id={option} className="border-[#4B2D6E] text-[#E8722A]" />
                      <Label htmlFor={option} className="text-lg cursor-pointer flex-1">{option}</Label>
                    </div>
                  ))}
                </RadioGroup>
              )}

              {/* Checkbox Questions */}
              {screeningQuestions[currentQuestion].type === 'checkbox' && (
                <div className="space-y-3" data-testid="checkbox-group">
                  {screeningQuestions[currentQuestion].options.map((option) => (
                    <div key={option} className="flex items-center space-x-3 p-4 rounded-lg border-2 border-gray-200 hover:border-[#E8722A] transition-colors">
                      <Checkbox
                        id={option}
                        checked={(answers[screeningQuestions[currentQuestion].id] || []).includes(option)}
                        onCheckedChange={(checked) => handleCheckboxAnswer(screeningQuestions[currentQuestion].id, option, checked)}
                        className="border-[#4B2D6E] data-[state=checked]:bg-[#E8722A]"
                      />
                      <Label htmlFor={option} className="text-lg cursor-pointer flex-1">{option}</Label>
                    </div>
                  ))}
                </div>
              )}

              {/* Textarea Questions */}
              {screeningQuestions[currentQuestion].type === 'textarea' && (
                <Textarea
                  value={answers[screeningQuestions[currentQuestion].id] || ''}
                  onChange={(e) => handleAnswer(screeningQuestions[currentQuestion].id, e.target.value)}
                  placeholder={screeningQuestions[currentQuestion].placeholder || 'Your answer...'}
                  className="min-h-32 text-lg border-2 focus:border-[#4B2D6E] bg-[#FAFAFA]"
                  data-testid="textarea-input"
                />
              )}

              {/* Number Questions */}
              {screeningQuestions[currentQuestion].type === 'number' && (
                <Input
                  type="number"
                  value={answers[screeningQuestions[currentQuestion].id] || ''}
                  onChange={(e) => handleAnswer(screeningQuestions[currentQuestion].id, e.target.value)}
                  min={screeningQuestions[currentQuestion].min}
                  max={screeningQuestions[currentQuestion].max}
                  className="h-14 text-lg border-2 focus:border-[#4B2D6E] bg-[#FAFAFA]"
                  data-testid="number-input"
                />
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-6">
                <Button
                  variant="outline"
                  onClick={goToPrevQuestion}
                  disabled={currentQuestion === 0}
                  className="border-2 border-[#4B2D6E] text-[#4B2D6E] hover:bg-[#4B2D6E] hover:text-white"
                  data-testid="prev-btn"
                >
                  <ArrowLeft className="mr-2 h-5 w-5" />
                  Previous
                </Button>
                <Button
                  onClick={goToNextQuestion}
                  disabled={submitting}
                  className="bg-[#E8722A] hover:bg-[#D6621F] text-white font-bold px-8"
                  data-testid="next-btn"
                >
                  {submitting ? (
                    <>Processing...</>
                  ) : currentQuestion === screeningQuestions.length - 1 ? (
                    <>Submit Application</>
                  ) : (
                    <>
                      Next
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pause Confirmation */}
        {step === 'questions' && showPauseConfirm && (
          <Card className="animate-fade-in border-t-4 border-green-500" data-testid="pause-confirm-card">
            <CardContent className="pt-8 pb-8 text-center space-y-5">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-[#333333] font-[Lato]">Your Progress is Saved!</h2>
              <p className="text-[#666666] max-w-md mx-auto">
                {personalInfo.email ? (
                  <>We've sent a link to <strong className="text-[#333]">{personalInfo.email}</strong> so you can pick up right where you left off.</>
                ) : (
                  <>We've emailed you a link so you can pick up right where you left off.</>
                )}
              </p>
              <div className="bg-[#F5F5F5] rounded-lg p-4 max-w-sm mx-auto">
                <p className="text-sm text-[#666]">
                  <strong className="text-[#4B2D6E]">Progress:</strong> {currentQuestion + 1} of {screeningQuestions.length} questions completed
                </p>
              </div>
              <p className="text-sm text-[#999]">
                You can safely close this page. Check your email for the resume link.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                <Button
                  onClick={() => setShowPauseConfirm(false)}
                  className="bg-[#E8722A] hover:bg-[#D6621F] text-white font-bold px-8"
                  data-testid="resume-now-btn"
                >
                  <Play className="mr-2 h-4 w-4" />
                  Continue Now
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/careers')}
                  className="border-[#4B2D6E] text-[#4B2D6E]"
                  data-testid="exit-to-careers-btn"
                >
                  Exit to Careers Page
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Complete Step */}
        {step === 'complete' && (
          <Card className="animate-fade-in border-t-4 border-[#4CAF50]" data-testid="complete-card">
            <CardContent className="py-12 text-center space-y-6">
              <div className="w-24 h-24 bg-[#4CAF50]/10 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-12 w-12 text-[#4CAF50]" />
              </div>
              <h2 className="text-3xl font-bold text-[#4B2D6E] font-[Lato]">Thank You!</h2>
              <p className="text-lg text-[#666666] max-w-md mx-auto">
                We've received your application and will be in touch soon. We appreciate your interest in joining the {BRAND_NAME} family.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="bg-[#4B2D6E] hover:bg-[#3a2256] text-white font-bold px-8 py-3 rounded-full"
                data-testid="return-home-btn"
              >
                Return to Careers Page
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Disqualified Step */}
        {step === 'disqualified' && (
          <Card className="animate-fade-in border-t-4 border-[#FFA000]" data-testid="disqualified-card">
            <CardContent className="py-12 text-center space-y-6">
              <div className="w-24 h-24 bg-[#FFA000]/10 rounded-full flex items-center justify-center mx-auto">
                <HandHeart className="h-12 w-12 text-[#FFA000]" />
              </div>
              <h2 className="text-2xl font-bold text-[#4B2D6E] font-[Lato]">Thank You for Your Interest</h2>
              <p className="text-lg text-[#666666] max-w-md mx-auto">
                {disqualifyMessage}
              </p>
              <p className="text-[#666666]">
                We wish you all the best in your job search.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="bg-[#4B2D6E] hover:bg-[#3a2256] text-white font-bold px-8 py-3 rounded-full"
                data-testid="return-home-disqualified-btn"
              >
                Return to Careers Page
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ApplicationFlow;
